import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useSize } from 'ahooks';
import Button from 'components/Button';
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton';
import { selectMetadataKeyByRegion } from 'reducers/selectors/metadata';
import { getWorkflowStart } from 'action-creators/payway/workflowStart';
import { setCurrentPBI } from 'action-creators/payway/currentPayment/currentPBI';
import { GetRegion, PLAYER_SEGMENT, ANONYMOUS_USER } from 'constants.js';
import {
  checkIfChannelIsBlocked,
  checkIfProtectPaymentIsActive,
} from 'containers/EPG/actions/channelBlocker/blocker.actions';
import { getLinkActionPurchase } from '../../../../modules/CardView/components/SectionPurchase/SectionPurchaseUtil';
import {
  clearPbiInfo,
  clearVCardData,
  getPurchaseButtoninfoByGroupId,
} from '../../actions/content/content.actions';
import {
  selectVCardPBIState,
  selectVCardContent,
} from '../../selectors/vcard.selector';
import PlayButton from './components/PlayButton';
import TrailerButton from './components/TrailerButton';
import FavoriteButton from './components/FavoriteButton';
import ShareContentButton from './components/ShareButton';

import './PBISection.scss';
import { clearInfoSectionBookmark } from '../../actions/bookmarks/bookmarks.actions';
import { convertToArray } from '../../../../helpers/convertToArray';
import getContentAvailability, {
  getContentAvailabilityByButton,
} from '../../../../utils/getContentAvailability';
import getContentType from '../../../../utils/getContentType';
import deleteSymbols from '../../../../utils/deleteSymbols';
import { pushGTMEvent } from '../../../Player/utils/analytics';
import { unLockVodContentbyGroudId } from '../../actions/content/unLockVodContent.actions';
import { createSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { getTranslation } from '../../../../utils/getTranslation';
import { getActionPaymentLink } from 'containers/Vcard/utils/redirect';
import { useVodPaywayToken } from 'containers/VodPlayer/hooks/useVodPaywayToken';

function getParameterByName(name, url) {
  const newName = name.replace(/[\[\]]/g, '\\$&');
  const regex = new RegExp(`[?&]${newName}(=([^&#]*)|&|#|$)`);

  const results = regex.exec(url);
  if (!results) return null;
  if (!results[2]) return '';
  return decodeURIComponent(results[2].replace(/\+/g, ' '));
}

const getPurchaseButtonText = (desc, button, translations) => {
  let newDesc = desc;
  const isFreeOfferFromClaro =
    !button?.currency &&
    !button?.price &&
    !button?.linkworkflowstart?.includes('promogate') &&
    getTranslation(`${button?.bonus}_free_period`, translations);
  const textFromFreeOffer = `${getTranslation(
    button?.oneofferdesckey,
    translations,
  )}`;
  if (isFreeOfferFromClaro) {
    return textFromFreeOffer;
  }
  let durationText = '';
  if (button?.frequency > 0) {
    if (button?.periodicity === 'hour') {
      if (desc.includes('@frequency')) {
        newDesc = desc.replace('@frequency', button?.frequency);
      }
      if (
        !newDesc.at(-1) === 'h' &&
        !newDesc.includes('24') &&
        !newDesc.includes('48')
      ) {
        durationText = `${button?.frequency} ${getTranslation(
          button?.periodicity,
          translations,
        )?.charAt(0)}`;
      }
    }
  }
  return `${newDesc} ${durationText}`;
};

const renderButtonFromPbi = (button, translations) => {
  const isFreeOfferFromClaro =
    !button?.currency &&
    !button?.price &&
    !button?.linkworkflowstart?.includes('promogate') &&
    getTranslation(`${button?.bonus}_free_period`, translations);
  const isPromogateOffer = button?.linkworkflowstart?.includes('promogate');
  const renderFreeOffer = () => (
    <span
      className={'ActionButton__content__custom__container__offer'}
      style={{ lineHeight: 1 }}
    >
      {' '}
      {getTranslation(`${button?.bonus}_free_period`, translations)}
    </span>
  );
  const renderPromogateOffer = () => (
    <div className="ActionButton__content__custom__container__promotext">
      {getTranslation(
        'offer_button_title_subscription_promogate',
        translations,
      )}
    </div>
  );
  const renderAddonRentBuyOffer = () => (
    <div className="ActionButton__content__custom__container__price">
      <span>
        {button?.currency}
        {button?.price}
      </span>
      <span className="ActionButton__content__custom__container__price__iva">
        {getTranslation(
          `${
            button?.producttype
          }_subscriptionDescription_costTaxIncluded_label`,
          translations,
        )}
      </span>
    </div>
  );
  if (isFreeOfferFromClaro) return renderFreeOffer();
  if (isPromogateOffer) return renderPromogateOffer();
  return renderAddonRentBuyOffer();
};

const renderPurchaseButtons = (
  list,
  onClick,
  btnMetadata,
  size,
  translations,
) => {
  if (!list || !list?.button) return null;

  const buttons = convertToArray(list?.button);

  return buttons.map(b => {
    if (b.waspurchased === '1') return null;
    const colorKey = `${b.producttype}_wp0`;

    const desc =
      b?.oneofferdesckey &&
      getTranslation(b?.oneofferdesckey, translations) &&
      getTranslation(b?.oneofferdesckey, translations) !== ''
        ? getTranslation(b?.oneofferdesckey, translations)
        : '';
    return (
      <Button
        key={b.key}
        buttonType="action"
        onClick={() => onClick('purchase', b)}
        className="pbiButtons pbi"
      >
        <div
          className="ActionButton__content__custom"
          style={{
            backgroundColor:
              size.width <= 768
                ? btnMetadata[colorKey]?.background || '#4C6FA7'
                : 'unset',
          }}
        >
          <div
            className="ActionButton__content__custom__container"
            style={{
              backgroundColor:
                size.width <= 768
                  ? 'unset'
                  : btnMetadata[colorKey]?.background || '#4C6FA7',
            }}
          >
            <span>
              {size.width <= 768 &&
                getPurchaseButtonText(desc, b, translations)}
            </span>
            {renderButtonFromPbi(b, translations)}
          </div>
          {size.width > 768 && (
            <div style={{ width: '110px' }}>
              {getPurchaseButtonText(desc, b, translations)}
            </div>
          )}
        </div>
      </Button>
    );
  });
};

const PBISection = props => {
  const { className, groupId, isPlaying, userHasSession, changePlay } = props;
  const dispatch = useDispatch();
  const info = useSelector(selectVCardPBIState);
  const content = useSelector(selectVCardContent);
  const navigate = useNavigate();
  const location = useLocation();
  const { titleUri, hasPreview, rate, isSerie } = useSelector(
    selectVCardContent,
  );
  const user = useSelector(state => state.user);
  const asset = useSelector(state => state.asset);
  const translations = useSelector(state => state.translations);
  const buttonMeta = useSelector(
    selectMetadataKeyByRegion('purchasebutton_colors'),
  );
  const claroLiteConfig = useSelector(
    selectMetadataKeyByRegion('cv_advertising_config'),
  );
  const dom = document.querySelector('body');
  const size = useSize(dom);
  const isFreemiumUserNotLogged =
    user.userType === ANONYMOUS_USER && claroLiteConfig?.enable;

  useEffect(
    () => {
      if (info.infoPBI.groupId === groupId || !groupId) return;
      dispatch(getPurchaseButtoninfoByGroupId(groupId, claroLiteConfig));
    },
    [groupId],
  );

  const {
    infoPBI: { listButtons, downloadButton },
    isLoadingPBI,
  } = info;
  const { playButton } = useVodPaywayToken(info?.infoPBI);

  const buttons = convertToArray(listButtons?.button);
  const suscBanner = buttons?.find(
    b => b.oneoffertype === 'subscrition' && b.waspurchased === '0',
  );

  const redirectToPayment = button => {
    const isBuyRentContent =
      button?.oneoffertype === 'download_rent' ||
      button?.oneoffertype === 'rent' ||
      button?.oneoffertype === 'download_buy';
    const urlPlayer = `/${GetRegion()}${PLAYER_SEGMENT}${groupId}`;
    const urlVcard = `/${GetRegion()}/vcard/homeuser/${titleUri}/${groupId}`;

    const link = getActionPaymentLink({
      userType: user.userType,
      claroAdEnabled: claroLiteConfig?.enable,
    });

    if (button.linkworkflowstart) {
      dispatch(getWorkflowStart(button.linkworkflowstart));
    }
    dispatch(setCurrentPBI(button));
    let params = {
      player_url: urlPlayer,
      group_id: groupId,
    };

    /* This is a patch for código promocional
    it maybe coul aply for other payment method
    */
    if (
      getParameterByName(
        'selected_payment_method',
        button.linkworkflowstart,
      ) === 'promogate'
    ) {
      params.linkworkflowstart = button.linkworkflowstart;
    }

    /* ======= */

    if (user.userType === ANONYMOUS_USER) {
      params = {
        ...params,
        group_id: groupId,
        offerid: button?.offerid,
        oneoffertype: button.oneoffertype,
        oneofferdesc: button.oneofferdesc,
        linkworkflowstart: button.linkworkflowstart,
        urlVcard,
        contentTitle: content?.title,
      };

      dispatch(clearPbiInfo());
    }
    if (isFreemiumUserNotLogged) {
      params.anonymousUser = 'true';
      localStorage.setItem('object_id', button?.product_id);
      if (isBuyRentContent) {
        params.isBuyRentContent = 'true';
        localStorage.removeItem('object_id');
      }
    }

    console.log('LINK', link);
    dispatch(
      checkIfProtectPaymentIsActive({
        onSuccess: () =>
          navigate({
            pathname: link,
            search: `?${createSearchParams(params)}`,
          },{
             state: {
              screen:
                getParameterByName(
                  'selected_payment_method',
                  button.linkworkflowstart,
                ) === 'promogate'
                  ? 'promogate'
                  : 'checkout',
            },
          }),
      }),
    );
  };

  const handleActionButton = (eventType, button = {}) => {
    const contentAvailability = getContentAvailability(
      content.types,
      content.proveedorCode,
    );
    const contentType = getContentType(content);
    const interactionEventInfo = {
      event: 'interaction_content',
      content_section: 'vcard',
      item_id: content.group_id,
      content_name: content.title,
      content_type: contentType,
      content_category: deleteSymbols(
        content.genres
          .map(genre => genre.desc)
          .toString()
          .toLowerCase(),
      ),
      content_availability: contentAvailability.toString(),
    };
    const beginCheckoutEventInfo = {
      event: 'begin_checkout',
      item_id: content.group_id,
      item_name: content.title,
      item_category: contentType,
      item_category2: deleteSymbols(
        content.genres
          .map(genre => genre.desc)
          .toString()
          .toLowerCase(),
      ),
      item_category4: content.isSerie
        ? `temporada ${content.episode.season} | episodio ${
            content.episode.number
          }  ${content.episodeTitle}`
        : 'no aplica',
      quantity: 1,
      item_brand:
        content.proveedorCode === 'amco' ? 'clarovideo' : content.proveedorCode,
      item_list_name: 'vcard',
    };
    if (eventType === 'play') {
      const rateVodContent = JSON.stringify(rate);
      pushGTMEvent({ ...interactionEventInfo, interaction_type: 'reproducir' });
      dispatch(
        checkIfChannelIsBlocked({
          rating: rate.id,
          onSuccess: () => {
            localStorage.setItem('rateVodContent', rateVodContent);
            dispatch(clearInfoSectionBookmark());
            navigate(`/${GetRegion()}${PLAYER_SEGMENT}${groupId}`);
            dispatch(clearVCardData());
            dispatch(unLockVodContentbyGroudId(groupId));
          },
        }),
      );
    }
    if (eventType === 'trailer') {
      pushGTMEvent({
        ...interactionEventInfo,
        interaction_type: 'ver trailer',
      });
      dispatch(
        checkIfChannelIsBlocked({
          rating: rate.id,
          onSuccess: () =>
            navigate({
              pathname: `/${GetRegion()}${PLAYER_SEGMENT}${groupId}`,
              search: `?${createSearchParams({
                preview: true,
              })}`,
            }),
        }),
      );
    }
    if (eventType === 'download') {
      // history.push(`/download/${groupId}`);
    }
    if (eventType === 'purchase') {
      pushGTMEvent({
        ...beginCheckoutEventInfo,
        item_category3: getContentAvailabilityByButton(button),
        price: button.price,
      });

      return redirectToPayment(button);
    }
    if (eventType === 'whatincludes') {
      pushGTMEvent({
        ...beginCheckoutEventInfo,
        item_category3: getContentAvailabilityByButton(suscBanner),
        price: suscBanner.price,
      });
    }
  };

  const currencyString = () => {
    if (content?.types?.includes('susc')) {
      return `${suscBanner?.currency}${suscBanner?.price}${
        suscBanner?.currency && suscBanner?.price
          ? `${getTranslation(
              `subscription_${suscBanner?.producttype}_periodicitySeparator_${
                suscBanner?.periodicity
              }_label`,
              translations,
            )}${getTranslation(
              `subscription_${suscBanner?.producttype}_${
                suscBanner?.periodicity
              }_label`,
              translations,
            )}`
          : ''
      }`;
    }
    return `${suscBanner?.currency}${suscBanner?.price}
      ${getTranslation(
        `subscription_${suscBanner?.producttype}_periodicitySeparator_${
          suscBanner?.periodicity
        }_label`,
        translations,
      )}
    ${getTranslation(
      `subscription_${suscBanner?.producttype}_${
        suscBanner?.periodicity
      }_label`,
      translations,
    )}`;
  };

  return (
    <>
      {suscBanner && (
        <div className="PBIPromo fade__in">
          {translations[`${suscBanner?.family}_access_commercialInfo_label`]}{' '}
          {currencyString()}{' '}
          {getTranslation(
            `${
              suscBanner.producttype
            }_subscriptionDescription_costTaxIncluded_label`,
            translations,
          )}
          {'. '}
          <span>
            {getTranslation(
              `FichaContenido_Comercial_TextoPromocion_${suscBanner.family}`,
              translations,
            )}
          </span>
        </div>
      )}

      <div
        className={`PBISection fade__in ${className} ${
          isPlaying ? 'playing' : ''
        }`}
      >
        {isLoadingPBI ? (
          <SkeletonTheme color="#202020" highlightColor="#444">
            <Skeleton height={80} />
            <Skeleton height={40} count={2} />
          </SkeletonTheme>
        ) : (
          <>
            <PlayButton
              buttonConfig={playButton}
              onClick={handleActionButton}
              groupId={groupId}
              titleUri={titleUri}
            />
            {suscBanner && (
              <Button
                className="pbiButtons includes"
                onClick={() => {
                  handleActionButton('whatincludes');
                  navigate(
                    `/${GetRegion()}/whatincludes/family?f=${
                      suscBanner.family
                    }&groupId=${groupId}`,
                  );
                }}
              >
                <div className="ActionButton__content ActionButton__content__includes">
                  <p className="ActionButton__content__includes__c">
                    <span className="ActionButton__content__includes__title">
                      {getTranslation(
                        `${
                          suscBanner.family
                        }_access_title_button_subscriptionDescription`,
                        translations,
                      )}
                    </span>
                    <span className="ActionButton__content__includes__subtitle">
                      {getTranslation(
                        `${
                          suscBanner.family
                        }_access_description_button_subscriptionDescription`,
                        translations,
                      )}
                    </span>
                  </p>
                </div>
              </Button>
            )}
            {renderPurchaseButtons(
              listButtons,
              handleActionButton,
              buttonMeta,
              size,
              translations,
            )}
            <div className="PBISection__commons">
              <TrailerButton
                isVisible={hasPreview}
                onClick={handleActionButton}
              />
              {userHasSession || claroLiteConfig.enable ? (
                <FavoriteButton
                  groupId={groupId}
                  userHasSession={userHasSession}
                />
              ) : null}
              <ShareContentButton />
            </div>
          </>
        )}
      </div>
      {!!buttons?.length &&
        !suscBanner &&
        !isLoadingPBI &&
        isSerie &&
        playButton?.visible === '0' && (
          <div className="PBIPromoSerie">
            <span>
              {getTranslation(
                'cv_seasonbuy_access_additionalInfo_label',
                translations,
              )}
            </span>
          </div>
        )}
    </>
  );
};

PBISection.propTypes = {
  className: PropTypes.string,
  groupId: PropTypes.string,
  isPlaying: PropTypes.bool,
  userHasSession: PropTypes.string,
};

PBISection.defaultProps = {
  className: '',
  isPlaying: false,
  userHasSession: false,
};

export default PBISection;
